import {
  isInterrupt,
  isLethal,
  isPowerless,
  isRescue,
  type CardDef,
} from '../../../../core/card';
import { cardDescription } from '../../../../core/cardText';
import { ACID, LIGHT } from '../../../palette';
import { plateCode } from '../codes';
import { pt, type Pt } from '../geom';
import { halftoneDots } from '../hatch';
import { box, dot, label, line, solid, type Paint, type Prim } from '../spec';
import { cjkTitle, latinTitle } from './util';
import { t, type TextKey } from '../../../../i18n';

/**
 * **单据 / 断裂 / 归还三个原型共用的纸面骨架。**
 *
 * 它们共用同一条页眉（版号 + 分类 + 一道细线）和同一段底部信息块，
 * 差别只在中间的图形。这样十一张牌摆一排时，**读起来是同一份报纸里的
 * 不同版面**，而不是十一张各画各的卡。
 */

/**
 * 牌的**分类字样**。
 *
 * ⚠️ 先看 `traits`，查不到才查表 —— 扩展包塞进来的新牌只要声明了特征
 * 就自动有分类，不用改这里。表里只有 `traits` **说不出**的那一个：
 * `Signal` 是攻击牌，但它的特征是"没有特征"（就是一张普通主动牌）。
 */
const CATEGORY_FALLBACK: Readonly<Record<string, TextKey>> = {
  Signal: 'render.category.attack',
};

export function categoryOf(card: CardDef): string {
  if (isLethal(card)) return t('render.category.lethal');
  if (isRescue(card)) return t('render.category.rescue');
  if (isInterrupt(card)) return t('render.category.interrupt');
  if (isPowerless(card)) return t('render.category.powerless');
  if (card.traits.needsTarget === true) return t('render.category.needsTarget');
  return t(CATEGORY_FALLBACK[card.local] ?? 'render.category.function');
}

/** 单据系列的页眉：版号在左、分类在右、底下一条细线 */
export function topStrip(card: CardDef, ink: number): Prim[] {
  return [
    label(plateCode(card.local), 6, 10.4, { size: 4.2, color: ink, weight: 700, tracking: 0.9 }),
    label(categoryOf(card), 94, 10.4, {
      size: 4.1,
      color: ink,
      role: 'cjk',
      anchor: 'end',
      tracking: 0.9,
    }),
    line([pt(0, 12), pt(100, 12)], { width: 0.45, color: ink, alpha: 0.9 }),
  ];
}

/**
 * 用**网点**填一块多边形。
 *
 * ⚠️ 点阵是算出来的几何（`halftoneDots` 逐个做"点在多边形内"判定），
 * 不是贴图 —— 所以点永远不会漏到形状外面，也和分辨率无关。
 */
export function halftoneFill(
  poly: readonly Pt[],
  paint: Paint,
  pitch: number,
  angleDeg: number,
  coverage: number,
): Prim[] {
  return halftoneDots(poly, pitch, angleDeg, coverage).map((d) => dot(d.x, d.y, d.r, paint));
}

/** 背景上一层极淡的网点，像纸本身的纹理 */
export function faintGrid(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  step: number,
  r: number,
  color: number,
  alpha: number,
): Prim[] {
  const out: Prim[] = [];
  for (let y = y0; y < y1; y += step) {
    for (let x = x0; x < x1; x += step) out.push(dot(x, y, r, solid(color, alpha)));
  }
  return out;
}

/**
 * 单据系列的底部信息块 + 页眉。中间的 `art` 由各牌自己给。
 *
 * 版式来自 `docs/card-faces.md` §5 的打样：左边一条语义色竖条、牌名、
 * 机读英文名、分隔线、规则正文、最底一条语义色横条。
 */
export function docketFrame(card: CardDef, ink: number, art: readonly Prim[]): Prim[] {
  return [
    ...topStrip(card, ink),
    box(0, 94, 100, 46, solid(0x000000, 0.8)),
    box(0, 94, 4, 30, solid(ink)),
    label(cjkTitle(card), 8, 107, {
      size: 13.5,
      color: LIGHT.base,
      role: 'cjk',
      weight: 900,
      tracking: 1.4,
      wrap: 7,
      lineHeight: 14,
    }),
    label(latinTitle(card), 8, 113.5, { size: 3.4, color: ink, tracking: 2.4 }),
    line([pt(0, 117.5), pt(100, 117.5)], { width: 0.45, color: ink, alpha: 0.5 }),
    label(cardDescription(card), 8, 124.5, {
      size: 3.5,
      color: LIGHT.base,
      role: 'cjk',
      wrap: 25,
      lineHeight: 4.9,
      alpha: 0.9,
    }),
    box(0, 135, 100, 5, solid(ink)),
    ...art,
  ];
}

/** 单据系列的语义色。⚠️ 按**效果**给，不按牌名 —— 但效果没有 trait 可依，所以是表 */
const DOCKET_INK: Readonly<Record<string, number>> = {
  // 攻击 → 警告黄。它让下家多打一个回合，是唯一一张直接冲人的牌
  Signal: ACID.amber,
  Suspend: ACID.sky,
  Await: ACID.cyan,
  // 评审定的：哈希 → **酸品红**（它动的是"顺序"，和别的不一样）
  Hash: ACID.magenta,
  // 评审定的：调试 → **酸紫**（它给的是"看穿"的信息）
  Debug: ACID.violet,
};

export function docketInk(card: CardDef): number {
  return DOCKET_INK[card.local] ?? ACID.cyan;
}
